/**
 * Misc fees — underwriter-defined fee lines on the quote (OR-1494 /
 * backend OR-1421). Each is a free-text name plus a dollar amount; a
 * submission can carry any number of them.
 *
 * Model: 1:N children of the quote row at
 * `/v2/submissions/{id}/quote/misc-fees/`. The quote GET embeds the current
 * list read-only as `misc_fees` so the page renders in one call; writes go
 * through the CRUD endpoints (values sent under `misc_fees` on the quote PUT
 * are ignored by the backend). Creating a fee before the quote has ever
 * been saved returns 404 — the quote is a PUT-upsert singleton.
 *
 * The three predefined fees (MCare Fee, Indiana / Louisiana PCF Surcharge)
 * are NOT misc fees: they persist on the quote's own fixed columns
 * (`mcare_fee`, `indiana_pcf_surcharge`, `louisiana_pcf_surcharge`, OR-1315).
 */

export interface MiscFeeWire {
    id: string;
    submission_id: string;
    /** ≤255 chars; nullable on the wire (documents print "Miscellaneous Fee"). */
    name: string | null;
    /** Decimal string, ≥ 0, 2 places; nullable (documents print a blank). */
    amount: string | null;
    created_at: string;
    updated_at: string;
}

/** FE-facing fee line. `amount` is a number for the Final Premium math;
 *  null when the backend has no amount for the row. */
export interface MiscFee {
    id: string;
    name: string;
    amount: number | null;
}

/** POST / PATCH body — both fields optional on the wire. */
export interface MiscFeeWritePayload {
    name?: string | null;
    amount?: number | null;
}

export function wireToMiscFee(w: MiscFeeWire): MiscFee {
    const n = w.amount === null ? NaN : Number(w.amount);
    return {
        id: w.id,
        name: w.name ?? "",
        amount: Number.isFinite(n) ? n : null,
    };
}

/**
 * Thrown by the page when one or more custom fee rows could not be created.
 * Carries exactly the rows that failed so the modal can keep just those for a
 * retry, instead of re-sending (and duplicating) the ones that did save.
 */
export class CustomFeeSaveError extends Error {
    readonly failed: { name: string; amount: number }[];

    constructor(failed: { name: string; amount: number }[]) {
        super("Failed to save fees");
        this.name = "CustomFeeSaveError";
        this.failed = failed;
    }
}

/** Σ amount over the fees that carry one — the figure Final Premium adds. */
export function sumMiscFeeAmounts(fees: readonly MiscFee[]): number {
    return fees.reduce((sum, f) => sum + (f.amount ?? 0), 0);
}
